<template>
  <div class="barcode-creator">
    <h1>Barcodes Labels</h1>

    <div class="tabs">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'batch' }"
        @click="setActiveTab('batch')"
      >
        Select Batch
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'current' }"
        @click="setActiveTab('current')"
      >
        Current Labels
      </button>
    </div>
    
    <template v-if="activeTab === 'batch'">
      <!-- Batch Selection/Creation -->
      <div class="batch-section">
        <div class="batch-selector">
          <label>Select Batch:</label>
          <div class="batch-search-container">
            <input
              v-model="batchSearchTerm"
              @input="onBatchSearch"
              @focus="onBatchSearchFocus"
              @blur="hideBatchDropdownDelayed"
              class="batch-input"
              placeholder="Start typing to search batches..."
              autocomplete="off"
            />
            <div
              v-if="showBatchDropdown && ((batches && batches.length > 0) || batchSearchLoading || (batchSearchTerm && batchSearchTerm.length >= 1 && !batchSearchLoading && (!batches || batches.length === 0)))"
              class="batch-dropdown"
            >
              <div v-if="batchSearchLoading" class="batch-dropdown-item loading">
                Searching...
              </div>
              <div v-else-if="(!batches || batches.length === 0) && batchSearchTerm && batchSearchTerm.length >= 1" class="batch-dropdown-item no-results">
                No batches found
              </div>
              <div
                v-else
                v-for="batch in batches"
                :key="batch.id"
                class="batch-dropdown-item"
                @mousedown.prevent="selectBatch(batch)"
              >
                <span class="batch-name">{{ batch.filename }} ({{ getBatchItemCount(batch) }} items)</span>
              </div>
            </div>
          </div>
          <button @click="showCreateBatch = true" class="btn btn-secondary">New Batch</button>
          <button v-if="selectedBatchId" @click="clearSelectedBatch" class="btn btn-secondary">Clear</button>
        </div>

      </div>

      <!-- Manual Box Entry Form -->
      <div v-if="currentBatch" class="entry-section">
        <h2>Add Boxes to Batch: {{ currentBatch.filename }}</h2>
        
        <div class="entry-form">
          <div class="form-row">
            <div class="form-group">
              <label for="label-date">Date:</label>
              <input
                id="label-date"
                type="date"
                v-model="labelDateFormatted"
                @change="updateBatchDateFromPicker"
                class="date-input"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group product-search-container">
              <label>Product Number *</label>
              <input
                v-model="productSearchTerm"
                @input="onProductSearch"
                @focus="showProductDropdown = true"
                @blur="hideProductDropdownDelayed"
                class="form-input"
                placeholder="Type to search..."
                autocomplete="off"
              />
              <!-- Product Search Dropdown -->
              <div v-if="showProductDropdown && ((productSearchResults && productSearchResults.length > 0) || productSearchLoading || (productSearchTerm && productSearchTerm.length >= 2 && !productSearchLoading && (!productSearchResults || productSearchResults.length === 0)))" class="product-dropdown">
                <div v-if="productSearchLoading" class="product-dropdown-item loading">
                  Searching...
                </div>
                <div v-else-if="(!productSearchResults || productSearchResults.length === 0) && productSearchTerm && productSearchTerm.length >= 2" class="product-dropdown-item no-results">
                  No items found
                </div>
                <div
                  v-else
                  v-for="product in productSearchResults"
                  :key="product.ProductId || product.id"
                  class="product-dropdown-item"
                  @mousedown.prevent="selectProduct(product)"
                >
                  <span class="product-number">{{ product.Code || product.ProductNumber || product.product_number }}</span>
                  <span class="product-description">{{ product.Description || product.description }}</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label>Description</label>
              <input v-model="newBox.description" class="form-input" readonly />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>Start Box *</label>
              <input v-model.number="newBox.start_box" type="number" class="form-input" required />
            </div>
            <div class="form-group">
              <label>End Box *</label>
              <input v-model.number="newBox.end_box" type="number" class="form-input" required />
            </div>
            <div class="form-group">
              <label>Qty *</label>
              <input
                v-model.number="newBox.qty_per_carton"
                type="number"
                step="1"
                min="1"
                @input="enforceInteger($event, 'qty_per_carton')"
                class="form-input"
                required
              />
            </div>
          </div>

          <div class="form-info">
            <p><strong>Note:</strong> This will create one label per box number in the range. For example, Start Box 1 and End Box 5 will create 5 labels (Box 1, Box 2, Box 3, Box 4, Box 5).</p>
          </div>

          <button @click="addBox" class="btn btn-primary">Add Boxes</button>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="clearError" class="btn btn-sm">Dismiss</button>
        </div>
      </div>

      <!-- Data Table Section -->
      <div v-if="currentBatch" class="data-section">
        <div class="batch-info">
          <div class="batch-details">
            <h2>Batch: {{ currentBatch.filename }}</h2>
            <p>Items: <strong>{{ items.length }}</strong></p>
          </div>
          <div class="batch-actions">
            <button 
              @click="generateLabels" 
              class="btn btn-primary"
              :disabled="loading || items.length === 0"
            >
              {{ loading ? 'Generating...' : 'Create Labels' }}
            </button>
            <button @click="deleteBatch" class="btn btn-danger">Delete Batch</button>
          </div>
        </div>

        <!-- Editable Table -->
        <div v-if="items.length > 0" class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Product #</th>
                <th>Description</th>
                <th>Box #</th>
                <th>Qty/Carton</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>
                  <input 
                    v-model="item.product_number" 
                    @blur="updateItem(item)"
                    class="table-input"
                  />
                </td>
                <td>
                  <input 
                    v-model="item.description" 
                    @blur="updateItem(item)"
                    class="table-input"
                  />
                </td>
                <td>
                  <input 
                    v-model="item.box_number"
                    @blur="updateItem(item)"
                    @input="enforceAlphanumeric($event, item, 'box_number')"
                    type="text"
                    class="table-input"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="item.qty_per_carton" 
                    @blur="updateItem(item)"
                    @input="enforceIntegerOnItem($event, item, 'qty_per_carton')"
                    type="number"
                    step="1"
                    min="1"
                    class="table-input"
                  />
                </td>
                <td>
                  <button 
                    @click="deleteItem(item)" 
                    class="btn btn-danger btn-sm"
                    title="Delete item"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state message -->
        <div v-else class="empty-state">
          <p>No items in this batch. Add boxes using the form above.</p>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="entry-section">
        <h2>Current Labels</h2>

        <div class="entry-form">
          <div class="form-row">
            <div class="form-group">
              <label>Box #</label>
              <input
                v-model="scanDetailFilters.boxnum"
                @keyup.enter="searchScanDetail"
                class="form-input"
                placeholder="Search by box #"
              />
            </div>
            <div class="form-group">
              <label>Bin</label>
              <input
                v-model="scanDetailFilters.bin"
                @keyup.enter="searchScanDetail"
                class="form-input"
                placeholder="Search by bin"
              />
            </div>
            <div class="form-group">
              <label>Location</label>
              <input
                v-model="scanDetailFilters.location"
                @keyup.enter="searchScanDetail"
                class="form-input"
                placeholder="Search by location"
              />
            </div>
          </div>

          <div class="scan-detail-actions">
            <button @click="searchScanDetail" class="btn btn-primary">Search</button>
            <button @click="clearScanDetailFilters" class="btn btn-secondary">Clear</button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="clearError" class="btn btn-sm">Dismiss</button>
        </div>
      </div>

      <div class="data-section">
        <div class="batch-info">
          <div class="batch-details">
            <h2>Results</h2>
            <p>
              Items: <strong>{{ scanDetailRows.length }}</strong>
              <span v-if="scanDetailMeta.total"> (Total: {{ scanDetailMeta.total }})</span>
            </p>
          </div>
          <div class="batch-actions">
            <button
              @click="reprintSelectedLabels"
              class="btn btn-primary"
              :disabled="selectedScanDetailKeys.length === 0 || loading"
            >
              Reprint Selected
            </button>
          </div>
        </div>

        <div class="pagination-controls" v-if="scanDetailTotalPages > 1 || scanDetailPerPageOptions.length > 0">
          <label class="pagination-label">
            Results
            <select
              v-model.number="scanDetailPerPage"
              @change="onScanDetailPerPageChange"
              class="pagination-select"
            >
              <option v-for="size in scanDetailPerPageOptions" :key="size" :value="size">
                {{ size }}
              </option>
            </select>
          </label>
          <button
            class="btn btn-secondary"
            :disabled="scanDetailPage <= 1 || loading"
            @click="goToScanDetailPage(scanDetailPage - 1)"
          >
            Prev
          </button>
          <label class="pagination-label">
            Page
            <select
              v-model.number="scanDetailPage"
              @change="onScanDetailPageChange"
              class="pagination-select"
            >
              <option v-for="page in scanDetailPageOptions" :key="page" :value="page">
                {{ page }}
              </option>
            </select>
            of {{ scanDetailTotalPages }}
          </label>
          <button
            class="btn btn-secondary"
            :disabled="scanDetailPage >= scanDetailTotalPages || loading"
            @click="goToScanDetailPage(scanDetailPage + 1)"
          >
            Next
          </button>
        </div>

        <div v-if="scanDetailRows.length > 0" class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    :checked="allScanDetailSelected"
                    @change="toggleAllScanDetailSelection"
                  />
                </th>
                <th>Partnum</th>
                <th>Box #</th>
                <th>Qty</th>
                <th>Bin</th>
                <th>YrMn</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in scanDetailRows" :key="getScanDetailKey(row, index)">
                <td>
                  <input
                    type="checkbox"
                    :checked="isScanDetailSelected(row, index)"
                    @change="toggleScanDetailSelection(row, index)"
                  />
                </td>
                <td>{{ getScanPartnum(row) }}</td>
                <td>{{ getScanBoxNum(row) }}</td>
                <td>{{ getScanQty(row) }}</td>
                <td>{{ getScanBin(row) }}</td>
                <td>{{ getScanYrMn(row) }}</td>
                <td>{{ getScanLocation(row) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <p>No scan detail found. Try adjusting your search.</p>
        </div>
      </div>
    </template>

<!-- Create Batch Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateBatch"
        class="barcode-modal-overlay"
        @click="showCreateBatch = false"
        style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 99999;"
      >
        <div
          class="barcode-modal-content"
          @click.stop
          style="background: white; padding: 30px; border-radius: 8px; min-width: 400px; max-width: 90vw; box-shadow: 0 10px 25px rgba(0,0,0,0.3); display: block; position: relative;"
        >
          <h3 style="margin-top: 0; margin-bottom: 20px; color: #333;">Create New Batch</h3>
          <input
            v-model="newBatchName"
            placeholder="Batch name"
            class="barcode-modal-input"
            style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box;"
            @keyup.enter="createBatch"
          />
          <div class="barcode-modal-actions" style="display: flex; gap: 10px; margin-top: 20px; justify-content: flex-end;">
            <button @click="createBatch" class="btn btn-primary" style="padding: 10px 20px; border: none; border-radius: 4px; font-size: 14px; cursor: pointer; background: #4CAF50; color: white;">Create</button>
            <button @click="showCreateBatch = false" class="btn btn-secondary" style="padding: 10px 20px; border: none; border-radius: 4px; font-size: 14px; cursor: pointer; background: #757575; color: white;">Cancel</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Loading Overlay -->
    <Teleport to="body">
      <div
        v-if="loading"
        class="barcode-loading-overlay"
        style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(255,255,255,0.8); display: flex; align-items: center; justify-content: center; z-index: 99998;"
      >
        <div style="text-align: center;">
          <div class="barcode-spinner" style="width: 50px; height: 50px; border: 4px solid #f3f3f3; border-top: 4px solid #4CAF50; border-radius: 50%; animation: barcode-spin 1s linear infinite; margin: 0 auto 15px;"></div>
          <p style="margin: 0; font-size: 16px; color: #333; font-weight: 500;">Loading...</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'BarcodeCreator',
  setup() {
    const store = useStore();
    
    // Computed properties from Vuex store
    // Note: Adjust the namespace if you named the module differently in store/index.js
    const batches = computed(() => store.state.BarcodeStoreVuex.batches);
    const currentBatch = computed(() => store.state.BarcodeStoreVuex.currentBatch);
    const items = computed(() => store.state.BarcodeStoreVuex.items);
    const loading = computed(() => store.state.BarcodeStoreVuex.loading);
    const error = computed(() => store.state.BarcodeStoreVuex.error);
    const productSearchResults = computed(() => store.state.BarcodeStoreVuex.productSearchResults || []);
    const productSearchLoading = computed(() => store.state.BarcodeStoreVuex.productSearchLoading || false);
    const scanDetailRows = computed(() => store.state.BarcodeStoreVuex.scanDetailRows || []);
    const scanDetailMeta = computed(() => store.state.BarcodeStoreVuex.scanDetailMeta || {});

    const activeTab = ref('batch');
    const selectedBatchId = ref('');
    const showCreateBatch = ref(false);
    const newBatchName = ref('');
    const labelDateFormatted = ref(''); // For date picker (YYYY-MM-DD format)
    const productSearchTerm = ref('');
    const showProductDropdown = ref(false);
    let searchTimeout = null;
    let skipNextSearch = false;
    const batchSearchTerm = ref('');
    const showBatchDropdown = ref(false);
    const batchSearchLoading = ref(false);
    let batchSearchTimeout = null;
    let skipNextBatchSearch = false;
    const scanDetailFilters = ref({
      boxnum: '',
      bin: '',
      location: '',
    });
    const scanDetailPage = ref(1);
    const scanDetailPerPage = ref(25);
    const scanDetailPerPageOptions = ref([25, 50, 100, 200, 500, 1000]);
    const selectedScanDetailKeys = ref([]);
    const selectedScanDetailScanStrs = ref([]);
    const selectAllScanDetail = ref(false);

    // Format today's date as M/D/YYYY
    const formatDate = (date) => {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    // Convert M/D/YYYY to YYYY-MM-DD for date input
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
      return '';
    };

    // Convert YYYY-MM-DD to M/D/YYYY
    const formatDateFromInput = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        return `${month}/${day}/${year}`;
      }
      return '';
    };

    const newBox = ref({
      product_number: '',
      description: '',
      start_box: 1,
      end_box: 1,
      qty_per_carton: 0
    });

    // Product search with debounce
    const onProductSearch = () => {
      // Skip search if flag is set (after product selection)
      if (skipNextSearch) {
        skipNextSearch = false;
        return;
      }

      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Debounce the search
      searchTimeout = setTimeout(async () => {
        if (productSearchTerm.value.length >= 2) {
          await store.dispatch('BarcodeStoreVuex/searchProducts', productSearchTerm.value);
          showProductDropdown.value = true;
        } else {
          store.dispatch('BarcodeStoreVuex/clearProductSearch');
        }
      }, 300);
    };

    const selectProduct = (product) => {
      // Set the product number using Code field and description
      const productCode = product.Code || product.ProductNumber || product.product_number;
      newBox.value.product_number = productCode;
      newBox.value.description = product.Description || product.description || '';

      // Set flag to skip the next search triggered by changing the input value
      skipNextSearch = true;
      productSearchTerm.value = productCode;

      // Clear search results and hide dropdown
      store.dispatch('BarcodeStoreVuex/clearProductSearch');
      showProductDropdown.value = false;
    };

    const hideProductDropdownDelayed = () => {
      // Delay hiding to allow click on dropdown items
      setTimeout(() => {
        showProductDropdown.value = false;
      }, 200);
    };

    // Note: All dispatch/commit calls use 'BarcodeStoreVuex' namespace to match the module name in store/index.js
    const loadBatches = async () => {
      try {
        await store.dispatch('BarcodeStoreVuex/fetchBatches', {
          page: 1,
          perPage: 10,
          filename: '',
          silent: true,
        });
      } catch (err) {
        console.error('Load batches error:', err);
      }
    };

    const loadScanDetail = async () => {
      try {
        if (!selectAllScanDetail.value) {
          selectedScanDetailKeys.value = [];
          selectedScanDetailScanStrs.value = [];
        }
        const response = await store.dispatch('BarcodeStoreVuex/fetchScanDetail', {
          page: scanDetailPage.value,
          perPage: scanDetailPerPage.value,
          boxnum: scanDetailFilters.value.boxnum,
          bin: scanDetailFilters.value.bin,
          location: scanDetailFilters.value.location,
        });
        if (selectAllScanDetail.value && response?.rows) {
          selectedScanDetailKeys.value = response.rows.map((row, index) => getScanDetailKey(row, index));
        }
      } catch (err) {
        console.error('Load scan detail error:', err);
      }
    };

    const searchScanDetail = async () => {
      scanDetailPage.value = 1;
      await loadScanDetail();
    };

    const clearScanDetailFilters = async () => {
      scanDetailFilters.value = {
        boxnum: '',
        bin: '',
        location: '',
      };
      scanDetailPage.value = 1;
      selectAllScanDetail.value = false;
      selectedScanDetailScanStrs.value = [];
      await loadScanDetail();
    };

    const scanDetailTotalPages = computed(() => {
      const totalPages = Number(scanDetailMeta.value?.totalPages);
      if (totalPages && totalPages > 0) {
        return totalPages;
      }
      const total = Number(scanDetailMeta.value?.total);
      const perPage = Number(scanDetailMeta.value?.perPage || scanDetailPerPage.value);
      if (total && perPage) {
        return Math.max(1, Math.ceil(total / perPage));
      }
      return 1;
    });

    const scanDetailPageOptions = computed(() => {
      const total = scanDetailTotalPages.value;
      return Array.from({ length: total }, (_, index) => index + 1);
    });

    const goToScanDetailPage = async (page) => {
      const total = scanDetailTotalPages.value;
      const nextPage = Math.min(Math.max(page, 1), total);
      if (nextPage === scanDetailPage.value) {
        return;
      }
      scanDetailPage.value = nextPage;
      await loadScanDetail();
    };

    const onScanDetailPageChange = async () => {
      await loadScanDetail();
    };

    const onScanDetailPerPageChange = async () => {
      scanDetailPage.value = 1;
      await loadScanDetail();
    };

    const loadBatch = async () => {
      if (!selectedBatchId.value) {
        store.commit('BarcodeStoreVuex/CLEAR_CURRENT_BATCH');
        labelDateFormatted.value = '';
        return;
      }

      try {
        await store.dispatch('BarcodeStoreVuex/getBatch', selectedBatchId.value);
        // Set label date, default to today if not set
        if (store.state.BarcodeStoreVuex.currentBatch?.label_date) {
          labelDateFormatted.value = formatDateForInput(store.state.BarcodeStoreVuex.currentBatch.label_date);
        } else {
          const today = formatDate(new Date());
          labelDateFormatted.value = formatDateForInput(today);
        }
      } catch (err) {
        console.error('Load batch error:', err);
      }
    };

    const onBatchSearch = () => {
      if (skipNextBatchSearch) {
        skipNextBatchSearch = false;
        return;
      }

      if (batchSearchTimeout) {
        clearTimeout(batchSearchTimeout);
      }

      batchSearchTimeout = setTimeout(async () => {
        batchSearchLoading.value = true;
        try {
          await store.dispatch('BarcodeStoreVuex/fetchBatches', {
            page: 1,
            perPage: 10,
            filename: batchSearchTerm.value || '',
            silent: true,
          });
          showBatchDropdown.value = true;
        } catch (err) {
          console.error('Batch search error:', err);
        } finally {
          batchSearchLoading.value = false;
        }
      }, 300);
    };

    const onBatchSearchFocus = async () => {
      showBatchDropdown.value = true;
      if (!batchSearchTerm.value) {
        batchSearchLoading.value = true;
        try {
          await store.dispatch('BarcodeStoreVuex/fetchBatches', {
            page: 1,
            perPage: 10,
            filename: '',
            silent: true,
          });
        } catch (err) {
          console.error('Load batch list error:', err);
        } finally {
          batchSearchLoading.value = false;
        }
      }
    };

    const selectBatch = async (batch) => {
      const batchName = batch.filename || batch.name || '';
      const itemCount = getBatchItemCount(batch);
      selectedBatchId.value = batch.id?.toString() || '';
      skipNextBatchSearch = true;
      batchSearchTerm.value = `${batchName} (${itemCount} items)`;
      showBatchDropdown.value = false;
      await loadBatch();
    };

    const clearSelectedBatch = () => {
      selectedBatchId.value = '';
      batchSearchTerm.value = '';
      store.commit('BarcodeStoreVuex/CLEAR_CURRENT_BATCH');
      labelDateFormatted.value = '';
    };

    const hideBatchDropdownDelayed = () => {
      setTimeout(() => {
        showBatchDropdown.value = false;
      }, 200);
    };

    const getBatchItemCount = (batch) => {
      return (
        batch?.items?.length ??
        batch?.item_count ??
        batch?.items_count ??
        batch?.count ??
        0
      );
    };

    const createBatch = async () => {
      if (!newBatchName.value.trim()) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Batch name is required');
        return;
      }

      try {
        // Default to today's date
        const todayDate = formatDate(new Date());
        const batch = await store.dispatch('BarcodeStoreVuex/createBatch', {
          name: newBatchName.value,
          vendor: 'Manual',
          labelDate: todayDate
        });
        selectedBatchId.value = batch.id.toString();
        await loadBatch();
        showCreateBatch.value = false;
        newBatchName.value = '';
      } catch (err) {
        console.error('Create batch error:', err);
      }
    };

    const updateBatchDateFromPicker = async () => {
      if (!store.state.BarcodeStoreVuex.currentBatch) {
        return;
      }

      // Convert from YYYY-MM-DD to M/D/YYYY
      const mdyDate = formatDateFromInput(labelDateFormatted.value);

      try {
        await store.dispatch('BarcodeStoreVuex/updateBatch', {
          batchId: store.state.BarcodeStoreVuex.currentBatch.id,
          batchData: {
            label_date: mdyDate || null
          },
          silent: true // Don't show loading overlay for date changes
        });
      } catch (err) {
        console.error('Update date error:', err);
      }
    };

    const addBox = async () => {
      if (!store.state.BarcodeStoreVuex.currentBatch) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Please select or create a batch first');
        return;
      }

      // Validation
      if (!newBox.value.product_number) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Product number is required');
        return;
      }
      if (newBox.value.start_box > newBox.value.end_box) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Start box must be <= End box');
        return;
      }
      if (newBox.value.qty_per_carton <= 0) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Qty must be greater than 0');
        return;
      }
      if (!Number.isInteger(newBox.value.qty_per_carton)) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'Qty must be a whole number');
        return;
      }

      try {
        await store.dispatch('BarcodeStoreVuex/createBoxes', {
          batchId: store.state.BarcodeStoreVuex.currentBatch.id,
          boxData: {
            batch_id: store.state.BarcodeStoreVuex.currentBatch.id,
            product_number: newBox.value.product_number,
            description: newBox.value.description || null,
            start_box: newBox.value.start_box,
            end_box: newBox.value.end_box,
            qty_per_carton: newBox.value.qty_per_carton
          }
        });

        // Reset form
        newBox.value = {
          product_number: '',
          description: '',
          start_box: 1,
          end_box: 1,
          qty_per_carton: 0
        };
        productSearchTerm.value = '';
      } catch (err) {
        console.error('Add box error:', err);
      }
    };

    const updateItem = async (item) => {
      // Recalculate carton_count
      item.carton_count = item.end_box - item.start_box + 1;

      try {
        await store.dispatch('BarcodeStoreVuex/updateItem', {
          itemId: item.id,
          itemData: {
            product_number: item.product_number,
            description: item.description,
            box_number: item.box_number,
            start_box: item.start_box,
            end_box: item.end_box,
            qty_per_carton: item.qty_per_carton,
            carton_count: item.carton_count,
          }
        });
      } catch (err) {
        console.error('Update error:', err);
      }
    };

    const deleteItem = async (item) => {
      if (!confirm('Are you sure you want to delete this item?')) {
        return;
      }

      try {
        await store.dispatch('BarcodeStoreVuex/deleteItem', item.id);
      } catch (err) {
        console.error('Delete error:', err);
      }
    };

    const generateLabels = async () => {
      try {
        // Get all item IDs from the current batch
        const allItemIds = store.state.BarcodeStoreVuex.items.map(item => item.id);

        const pdfBlob = await store.dispatch('BarcodeStoreVuex/generateLabels', {
          batchId: store.state.BarcodeStoreVuex.currentBatch.id,
          itemIds: allItemIds
        });
        store.dispatch('BarcodeStoreVuex/downloadPDF', {
          blob: pdfBlob,
          filename: `labels_${store.state.BarcodeStoreVuex.currentBatch.filename}.pdf`
        });
      } catch (err) {
        console.error('Generate error:', err);
      }
    };

    const deleteBatch = async () => {
      if (!store.state.BarcodeStoreVuex.currentBatch) {
        return;
      }

      const batchName = store.state.BarcodeStoreVuex.currentBatch.filename;
      const itemCount = store.state.BarcodeStoreVuex.items.length;
      
      const confirmMessage = `Are you sure you want to delete batch "${batchName}"?\n\n` +
        `This will permanently delete:\n` +
        `- The batch\n` +
        `- ${itemCount} item(s) in this batch\n\n` +
        `This action cannot be undone.`;

      if (!confirm(confirmMessage)) {
        return;
      }

      // Double confirmation
      if (!confirm('Final confirmation: Delete this batch?')) {
        return;
      }

      try {
        await store.dispatch('BarcodeStoreVuex/deleteBatch', store.state.BarcodeStoreVuex.currentBatch.id);
        selectedBatchId.value = '';
      } catch (err) {
        console.error('Delete batch error:', err);
      }
    };

    const clearError = () => {
      store.commit('BarcodeStoreVuex/SET_ERROR', null);
    };

    const setActiveTab = async (tab) => {
      activeTab.value = tab;
      if (tab === 'current') {
        await loadScanDetail();
      }
    };

    const getScanDetailKey = (row, index) => {
      const keyValue = row?.id ?? row?.ID ?? row?.scan_detail_id ?? row?.scanDetailId ?? row?.scan_id ?? row?.ScanId ?? row?.ScanDetailId;
      const scanStr = row?.ScanStr ?? row?.scan_str ?? row?.scanStr ?? row?.ScanString ?? row?.scan_string;
      return (keyValue ?? scanStr ?? `row-${index}`).toString();
    };

    const getScanStr = (row) => {
      return row?.ScanStr ?? row?.scan_str ?? row?.scanStr ?? row?.ScanString ?? row?.scan_string ?? '';
    };

    const getScanPartnum = (row) => {
      return row?.Partnum ?? row?.partnum ?? row?.PartNum ?? row?.part_num ?? row?.product_number ?? '';
    };

    const getScanBoxNum = (row) => {
      return row?.Boxnum ?? row?.BoxNum ?? row?.boxnum ?? row?.boxNum ?? row?.BoxNumber ?? row?.box_number ?? '';
    };

    const getScanQty = (row) => {
      return row?.Qty ?? row?.qty ?? row?.Quantity ?? row?.quantity ?? row?.qty_per_carton ?? '';
    };

    const getScanBin = (row) => {
      return row?.Bin ?? row?.bin ?? '';
    };

    const getScanYrMn = (row) => {
      return row?.YrMn ?? row?.yrmn ?? row?.YrMN ?? row?.yr_mn ?? row?.year_month ?? '';
    };

    const getScanLocation = (row) => {
      return row?.Location ?? row?.location ?? '';
    };

    const isScanDetailSelected = (row, index) => {
      const key = getScanDetailKey(row, index);
      return selectedScanDetailKeys.value.includes(key);
    };

    const toggleScanDetailSelection = (row, index) => {
      const key = getScanDetailKey(row, index);
      const current = selectedScanDetailKeys.value;
      const existingIndex = current.indexOf(key);
      if (existingIndex >= 0) {
        current.splice(existingIndex, 1);
      } else {
        current.push(key);
      }
    };

    const allScanDetailSelected = computed(() => {
      if (!scanDetailRows.value.length) return false;
      if (selectAllScanDetail.value) return true;
      return scanDetailRows.value.every((row, index) => selectedScanDetailKeys.value.includes(getScanDetailKey(row, index)));
    });

    const fetchAllScanDetailRows = async () => {
      const baseParams = {
        boxnum: scanDetailFilters.value.boxnum,
        bin: scanDetailFilters.value.bin,
        location: scanDetailFilters.value.location,
        commitResults: false,
      };

      const firstResponse = await store.dispatch('BarcodeStoreVuex/fetchScanDetail', {
        page: 1,
        perPage: 1000,
        ...baseParams,
      });

      const rows = firstResponse?.rows || [];
      const meta = firstResponse?.meta || {};
      const totalPages = meta.totalPages || Math.ceil((meta.total || rows.length) / (meta.perPage || 1000));

      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
          const nextResponse = await store.dispatch('BarcodeStoreVuex/fetchScanDetail', {
            page,
            perPage: 1000,
            ...baseParams,
          });
          if (nextResponse?.rows?.length) {
            rows.push(...nextResponse.rows);
          }
        }
      }

      return rows;
    };

    const toggleAllScanDetailSelection = async () => {
      if (selectAllScanDetail.value) {
        selectAllScanDetail.value = false;
        selectedScanDetailKeys.value = [];
        selectedScanDetailScanStrs.value = [];
        return;
      }

      selectAllScanDetail.value = true;
      try {
        const allRows = await fetchAllScanDetailRows();
        selectedScanDetailScanStrs.value = allRows.map((row) => getScanStr(row)).filter((value) => value);
        selectedScanDetailKeys.value = scanDetailRows.value.map((row, index) => getScanDetailKey(row, index));
      } catch (err) {
        selectAllScanDetail.value = false;
        console.error('Select all error:', err);
      }
    };

    const reprintSelectedLabels = async () => {
      let scanStrs = [];
      if (selectAllScanDetail.value) {
        scanStrs = selectedScanDetailScanStrs.value.slice();
      } else {
        const selectedRows = scanDetailRows.value.filter((row, index) => selectedScanDetailKeys.value.includes(getScanDetailKey(row, index)));
        scanStrs = selectedRows.map((row) => getScanStr(row)).filter((value) => value);
      }

      if (scanStrs.length === 0) {
        store.commit('BarcodeStoreVuex/SET_ERROR', 'No ScanStr values found for the selected rows');
        return;
      }

      try {
        const pdfBlob = await store.dispatch('BarcodeStoreVuex/generateLabels', {
          scanStrs,
        });
        const timestamp = new Date().toISOString().slice(0, 10);
        store.dispatch('BarcodeStoreVuex/downloadPDF', {
          blob: pdfBlob,
          filename: `labels_reprint_${timestamp}.pdf`,
        });
      } catch (err) {
        console.error('Reprint labels error:', err);
      }
    };

    // Enforce integer input - prevent decimals from being typed
    const enforceInteger = (event, fieldName) => {
      const value = event.target.value;
      // Remove any decimal point and everything after it
      const intValue = value.split('.')[0];
      if (value !== intValue) {
        event.target.value = intValue;
        newBox.value[fieldName] = parseInt(intValue) || 0;
      }
    };

    // Enforce integer input on table items
    const enforceIntegerOnItem = (event, item, fieldName) => {
      const value = event.target.value;
      // Remove any decimal point and everything after it
      const intValue = value.split('.')[0];
      if (value !== intValue) {
        event.target.value = intValue;
        item[fieldName] = parseInt(intValue) || 0;
      }
    };

    // Enforce alphanumeric input - only allow letters and numbers
    const enforceAlphanumeric = (event, item, fieldName) => {
      const value = event.target.value;
      // Remove any characters that are not letters or numbers
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      if (value !== alphanumericValue) {
        event.target.value = alphanumericValue;
        item[fieldName] = alphanumericValue;
      }
    };

    onMounted(() => {
      loadBatches();
    });

    return {
      batches,
      activeTab,
      selectedBatchId,
      batchSearchTerm,
      showBatchDropdown,
      batchSearchLoading,
      currentBatch,
      items,
      error,
      loading,
      scanDetailRows,
      scanDetailMeta,
      showCreateBatch,
      newBatchName,
      newBox,
      loadBatches,
      onBatchSearch,
      onBatchSearchFocus,
      selectBatch,
      clearSelectedBatch,
      hideBatchDropdownDelayed,
      getBatchItemCount,
      loadScanDetail,
      scanDetailPage,
      scanDetailPerPage,
      scanDetailPerPageOptions,
      scanDetailTotalPages,
      scanDetailPageOptions,
      goToScanDetailPage,
      onScanDetailPageChange,
      onScanDetailPerPageChange,
      searchScanDetail,
      clearScanDetailFilters,
      scanDetailFilters,
      setActiveTab,
      loadBatch,
      createBatch,
      addBox,
      updateItem,
      deleteItem,
      generateLabels,
      deleteBatch,
      labelDateFormatted,
      updateBatchDateFromPicker,
      clearError,
      formatDate,
      productSearchTerm,
      productSearchResults,
      productSearchLoading,
      showProductDropdown,
      onProductSearch,
      selectProduct,
      hideProductDropdownDelayed,
      enforceInteger,
      enforceIntegerOnItem,
      enforceAlphanumeric,
      getScanDetailKey,
      getScanStr,
      getScanPartnum,
      getScanBoxNum,
      getScanQty,
      getScanBin,
      getScanYrMn,
      getScanLocation,
      selectedScanDetailKeys,
      selectedScanDetailScanStrs,
      selectAllScanDetail,
      isScanDetailSelected,
      toggleScanDetailSelection,
      allScanDetailSelected,
      toggleAllScanDetailSelection,
      reprintSelectedLabels,
    };
  },
};
</script>

<style>
/* Modal - Non-scoped styles for Teleport (using unique class names to avoid AdminLTE conflicts) */
.barcode-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99999 !important;
}

.barcode-modal-content {
  background: white !important;
  padding: 30px !important;
  border-radius: 8px !important;
  min-width: 400px !important;
  max-width: 90vw !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;
  z-index: 100000 !important;
  position: relative !important;
  display: block !important;
}

.barcode-modal-content h3 {
  margin-top: 0 !important;
  margin-bottom: 20px !important;
  color: #333 !important;
}

.barcode-modal-input {
  width: 100% !important;
  padding: 10px !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  box-sizing: border-box !important;
}

.barcode-modal-input:focus {
  outline: none !important;
  border-color: #4CAF50 !important;
}

.barcode-modal-actions {
  display: flex !important;
  gap: 10px !important;
  margin-top: 20px !important;
  justify-content: flex-end !important;
}

.barcode-modal-actions .btn {
  padding: 10px 20px !important;
  border: none !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
}

.barcode-modal-actions .btn-primary {
  background: #4CAF50 !important;
  color: white !important;
}

.barcode-modal-actions .btn-secondary {
  background: #757575 !important;
  color: white !important;
}

.barcode-modal-actions .btn:hover {
  opacity: 0.9 !important;
}

/* Loading spinner animation */
@keyframes barcode-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.barcode-loading-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(255, 255, 255, 0.8) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99998 !important;
}

.barcode-spinner {
  width: 50px !important;
  height: 50px !important;
  border: 4px solid #f3f3f3 !important;
  border-top: 4px solid #4CAF50 !important;
  border-radius: 50% !important;
  animation: barcode-spin 1s linear infinite !important;
}
</style>

<style scoped>
.barcode-creator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

h1 {
  color: #333;
  margin-bottom: 30px;
}

h2 {
  color: #555;
  margin-bottom: 20px;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.tab-button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s, color 0.2s;
}

.tab-button.active {
  background: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

/* Batch Section */
.batch-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.batch-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.batch-selector label {
  font-weight: 600;
}

.batch-select {
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.batch-search-container {
  position: relative;
  flex: 1;
}

.batch-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.batch-input:focus {
  outline: none;
  border-color: #4CAF50;
}

.batch-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 220px;
  overflow-y: auto;
}

.batch-dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #eee;
}

.batch-dropdown-item:last-child {
  border-bottom: none;
}

.batch-dropdown-item:hover {
  background: #f5f5f5;
}

.batch-dropdown-item.loading,
.batch-dropdown-item.no-results {
  color: #666;
  font-style: italic;
  cursor: default;
}

.batch-dropdown-item.loading:hover,
.batch-dropdown-item.no-results:hover {
  background: white;
}

.batch-name {
  font-weight: 600;
  color: #333;
}

.batch-meta {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

/* Modal styles moved to non-scoped block above for Teleport support */

/* Product Search Dropdown */
.product-search-container {
  position: relative;
}

.product-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.product-dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #eee;
}

.product-dropdown-item:last-child {
  border-bottom: none;
}

.product-dropdown-item:hover {
  background: #f5f5f5;
}

.product-dropdown-item.loading,
.product-dropdown-item.no-results {
  color: #666;
  font-style: italic;
  cursor: default;
}

.product-dropdown-item.loading:hover,
.product-dropdown-item.no-results:hover {
  background: white;
}

.product-dropdown-item .product-number {
  font-weight: 600;
  color: #333;
}

.product-dropdown-item .product-description {
  font-size: 12px;
  color: #666;
}

/* Entry Form */
.entry-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
}

.entry-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-info {
  background: #e3f2fd;
  border-left: 4px solid #2196F3;
  padding: 12px;
  border-radius: 4px;
  margin: 10px 0;
}

.form-info p {
  margin: 0;
  font-size: 14px;
  color: #1976D2;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 5px;
  font-size: 14px;
}

.form-input {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-input:focus {
  outline: none;
  border-color: #4CAF50;
}

.error-message {
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
  color: #c62828;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Data Section */
.batch-info {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.batch-details {
  flex: 1;
}

.date-input {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;
  cursor: pointer;
}

.date-input:focus {
  outline: none;
  border-color: #4CAF50;
}

.batch-actions {
  display: flex;
  gap: 10px;
}

.batch-info h2 {
  margin: 0 0 10px 0;
  font-size: 18px;
}

.batch-info p {
  margin: 5px 0;
  font-size: 14px;
}

.scan-detail-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 20px;
}

.pagination-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.pagination-select {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.scan-str-cell {
  word-break: break-all;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}

/* Table */
.table-container {
  overflow-x: auto;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.data-table th {
  background: #4CAF50;
  color: white;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
}

.data-table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.data-table tr:hover {
  background: #f9f9f9;
}

.table-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.table-input:focus {
  outline: none;
  border-color: #4CAF50;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover {
  opacity: 0.9;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #4CAF50;
  color: white;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-danger {
  background: #f44336;
  color: white;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 20px 0;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
}
</style>
